/**
 * The pieces a removal step is built from.
 *
 * A leaf on purpose. Both halves of removal need these, and putting them in
 * either half made the two files import each other.
 */
import type { EnvironmentId } from "@cmmd-forge/contracts";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import { CmmdApiError } from "../../environments/cmmdApi/transport";
import {
  createCmmdControlPlaneClient,
  desktopCloudBootstrapMatchesRuntime,
  parsePositiveInteger,
  readDesktopCloudBootstrap,
} from "./sidebarProjectRemovalControlPlaneClient";
import { withRemoveProjectOperationTimeout } from "./sidebarCloudProjectRemovalSaga";

export type RemovalStepOutcome<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: unknown };

export async function settleRemovalStep<T>(
  operationLabel: string,
  operation: (signal: AbortSignal) => Promise<T>,
): Promise<RemovalStepOutcome<T>> {
  try {
    return {
      ok: true,
      value: await withRemoveProjectOperationTimeout(operationLabel, operation),
    };
  } catch (error) {
    return { ok: false, error };
  }
}

export function isProjectAlreadyGoneError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const tag = (error as { _tag?: unknown })._tag;
  const commandType = (error as { commandType?: unknown }).commandType;
  const detail = (error as { detail?: unknown }).detail;
  if (
    tag === "OrchestrationCommandInvariantError" &&
    commandType === "project.delete" &&
    typeof detail === "string" &&
    detail.includes("does not exist")
  ) {
    return true;
  }
  const message = (error as { message?: unknown }).message;
  return (
    typeof message === "string" && message.includes("does not exist for command 'project.delete'")
  );
}

export async function destroyCmmdRuntimeForEnvironment(
  environmentId: EnvironmentId,
  options: { readonly signal?: AbortSignal } = {},
): Promise<void> {
  // A durable `env_` id has no legacy runtime id to destroy against, so this
  // is a no-op for it: there is nothing here to translate a durable id into a
  // live runtime, and that translation is an async catalog lookup out of this
  // function's scope (see `environmentIdClassification.ts`'s header).
  const runtimeId = classifyEnvironmentId(environmentId).legacyRuntimeId;
  if (!runtimeId) return;

  const desktopCloudBootstrap = readDesktopCloudBootstrap();
  const matchingDesktopCloudBootstrap =
    desktopCloudBootstrap &&
    desktopCloudBootstrapMatchesRuntime(desktopCloudBootstrap, environmentId, runtimeId)
      ? desktopCloudBootstrap
      : null;
  const organizationId = parsePositiveInteger(matchingDesktopCloudBootstrap?.cmmdOrganizationId);
  const runtimeApi = createCmmdControlPlaneClient(desktopCloudBootstrap);

  try {
    await runtimeApi.destroyRuntime({
      runtimeId,
      purgeVolume: true,
      ...(organizationId !== undefined ? { organizationId } : {}),
      ...(options.signal !== undefined ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (error instanceof CmmdApiError && error.status === 404) return;
    throw error;
  }
}
